import * as React from 'react';
import { ConfigurationScope, Language, Template, CustomTemplateParams } from '../../Model';
import { Flex } from '../../CommonUI/Layout';
import { Select, LabelSize } from '../../CommonUI';
import { StyleSheet, css } from 'aphrodite';
import { TrashBin } from '../../Assets/images/svg';
import { Utils } from '../../Utils';

interface CustomEmailTemplatesProps {
    configurationScopeTree?: ConfigurationScope[];
    customTemplateParams: CustomTemplateParams[];
    templates: Template[];
    allowedDomains?: Set<string>;
    setCustomWebsite: (customTemplateParams: CustomTemplateParams[]) => void;
    setCustomTemplateLanguage: (customTemplateParams: CustomTemplateParams[]) => void;
    setCustomTemplateId: (customTemplateParams: CustomTemplateParams[]) => void;
}

export class CustomEmailTemplates extends React.Component<CustomEmailTemplatesProps> {
    constructor(props: CustomEmailTemplatesProps) {
        super(props);
        this.state = {
            customTemplateParams: [],
        };
        this.templateOptionsForLanguage = this.templateOptionsForLanguage.bind(this);
        this.onCustomWebsiteChange = this.onCustomWebsiteChange.bind(this);
        this.onCustomTemplateLanguageChange = this.onCustomTemplateLanguageChange.bind(this);
        this.onCustomTemplateIdChange = this.onCustomTemplateIdChange.bind(this);
        this.onCustomTemplateTrashBinClicked = this.onCustomTemplateTrashBinClicked.bind(this);
    }

    public templateOptionsForLanguage(language: string) {
        const templates = this.props.templates!;
        const currentLocaleTemplates = language ?
            templates.filter((tpl) => tpl.locale === language || !tpl.isDefaultTemplate) :
            templates.filter((tpl) => !tpl.isDefaultTemplate);

        return currentLocaleTemplates
            .filter((tpl) => !tpl.name.endsWith('(Deprecated)'))
            .sort((prev, next) => {
                return (prev.isDefaultTemplate === next.isDefaultTemplate) ? 0 : prev.isDefaultTemplate ? -1 : 1;
            });
    }

    public onCustomWebsiteChange(type: string, value: string[] | undefined, idx: number) {
        const { configurationScopeTree, customTemplateParams, setCustomWebsite } = this.props;

        const copy = customTemplateParams.slice();

        if (configurationScopeTree && value) {
            const scopeTree = configurationScopeTree.find((scope) => Utils.arraysEqual(scope.ids, value));
            if (scopeTree) {
                copy[idx].scopeTree = scopeTree;
                setCustomWebsite(copy);
            }
        }
    }

    public onCustomTemplateLanguageChange(type: string, value: string | undefined, idx: number) {
        if (value) {
            const templateLanguage = value as Language;
            const { customTemplateParams, setCustomTemplateLanguage } = this.props;

            const copy = customTemplateParams.slice();
            copy[idx].languageId = templateLanguage;

            const templateOptions = this.templateOptionsForLanguage(templateLanguage);
            const template = templateOptions.find((tpl) => tpl.id === copy[idx].templateId);
            if (!template) {
                copy[idx].templateId = templateOptions[0].id;
            }

            setCustomTemplateLanguage(copy);
        }
    }

    public onCustomTemplateIdChange(type: string, value: string, idx: number) {
        const templateId = value;
        const { customTemplateParams, setCustomTemplateId } = this.props;

        const copy = customTemplateParams.slice();
        copy[idx].templateId = templateId;

        setCustomTemplateId(copy);
    }

    public onCustomTemplateTrashBinClicked(event: React.MouseEvent<SVGSVGElement>, idx: number) {
        const { customTemplateParams, setCustomTemplateId } = this.props;

        const copy = customTemplateParams.slice();
        copy.splice(idx, 1);

        setCustomTemplateId(copy);
    }

    public onSvgClicked(next: (event: any, idx: number) => void, idx?: number) {
        return (event: any) => {
            return next(event, idx || 0);
        };
    }

    public onSelectChange(next: (type: any, value: any, idx: number) => void, idx?: number) {
        return (type: any, value: any) => {
            return next(type, value, idx || 0);
        };
    }

    public render() {
        const {
            configurationScopeTree,
            customTemplateParams,
            allowedDomains,
            templates,
        } = this.props;

        return (
            <Flex style={{width: '100%'}}>
                {
                    customTemplateParams.map((val, idx) => {
                        const customTemplateOptions = this.templateOptionsForLanguage(val.languageId)
                            .map((template) => ({ value: template.id, label: template.name }));
                        const customTemplateObject = templates!.find((template) => template.id === val.templateId);
                        const customTemplateValue = (customTemplateObject)
                            ? { value: customTemplateObject.id, label: customTemplateObject.name }
                            : { value: '', label: '' };

                        const website = {
                            label: `${val.scopeTree.names.store}${val.scopeTree.names.view ? '-' + val.scopeTree.names.view : ''}`,
                            value: val.scopeTree.ids
                        };
                        const websiteOptions = configurationScopeTree ?
                            configurationScopeTree.filter((scope) =>
                                !customTemplateParams.some((selected) =>
                                    Utils.arraysEqual(scope.ids, selected.scopeTree.ids)) &&
                                allowedDomains &&
                                allowedDomains.has(scope.domain)
                            ).map((item) => ({
                                label: `${item.names.store}${item.names.view ? '-' + item.names.view : ''}`,
                                value: item.ids,
                            }))
                            : [{ label: '', value: [''] }];

                        return (
                            <Flex
                                flexDirection={'row'}
                                alignItems={'flex-end'}
                                justifyContent={'flex-start'}
                                key={idx}
                                style={{width: '100%', marginTop: '24px'}}
                            >
                                <Select
                                    id="custom-language-select"
                                    mt="0"
                                    data-id={idx}
                                    label="invitations.section.template.templateCategoryLabel"
                                    labelSize={LabelSize.Small}
                                    localize={true}
                                    value={val.languageId}
                                    data={Object.keys(Language).map((type) => Language[type])}
                                    onChangeEvent={this.onSelectChange(this.onCustomTemplateLanguageChange, idx)}
                                />
                                <Select
                                    id="custom-email-template-select"
                                    mt="0"
                                    data-id={idx}
                                    label="invitations.section.template.templateLabel"
                                    labelSize={LabelSize.Small}
                                    value={customTemplateValue}
                                    data={customTemplateOptions}
                                    onChangeEvent={this.onSelectChange(this.onCustomTemplateIdChange, idx)}
                                />
                                <Select
                                    id="custom-all-websites"
                                    mt="0"
                                    data-id={idx}
                                    label="invitations.section.template.useTemplateLabel"
                                    labelSize={LabelSize.Small}
                                    value={website}
                                    data={websiteOptions}
                                    onChangeEvent={this.onSelectChange(this.onCustomWebsiteChange, idx)}
                                />
                                <TrashBin className={css(styles.menu)} onClick={this.onSvgClicked(this.onCustomTemplateTrashBinClicked, idx)} />
                            </Flex>
                        );
                    })
                }
            </Flex>
        );
    }
}

const styles = StyleSheet.create({
    menu: {
        'minWidth': '20px',
        'minHeight': '20px',
        'marginBottom': '10px',
        ':hover #trashbin': {
            stroke: 'black',
            transition: 'all ease 0.3s',
        },
    },
});
